import {
  ApiError,
  projectApi,
  type Project,
  type ProjectProfile,
  type WebsiteDiscovery,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Globe2,
  Layers2,
  Smartphone,
} from 'lucide-react';
import * as React from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  normalizedWebsite,
  setupHostnames,
  discoveryMatchesWebsite,
  parseAdditionalHostnames,
} from '@/lib/website-setup';
import { ProjectAvatar } from '@/components/project-avatar';
import { useWebsiteDiscovery } from '@/hooks/use-website-discovery';
import { DiscoveryProgress } from '@/components/discovery-progress';
import { useSession } from '@/hooks/use-session';
import { cn } from '@/lib/utils';
import { ProjectInstallation } from './installation';
import { GrowthSignals } from './growth-signals';

type Platform = NonNullable<ProjectProfile['platform']>;
const PLATFORMS = [
  {
    value: 'web' as const,
    label: 'Web',
    detail: 'Website or browser app',
    icon: Globe2,
  },
  {
    value: 'mobile' as const,
    label: 'Mobile',
    detail: 'iOS or Android app',
    icon: Smartphone,
  },
  {
    value: 'both' as const,
    label: 'Both',
    detail: 'Web and mobile together',
    icon: Layers2,
  },
];
const SETUP_STEPS = [
  'Website',
  'Your product',
  'Tracking & setup',
  'Integrations',
];

const SetupProgress = ({ current }: { current: number }) => (
  <ol className="grid grid-cols-4" aria-label="Project setup progress">
    {SETUP_STEPS.map((label, index) => {
      const complete = index < current;
      const active = index === current;
      return (
        <li
          key={label}
          aria-current={active ? 'step' : undefined}
          className="relative min-w-0 text-center"
        >
          {index < SETUP_STEPS.length - 1 && (
            <span
              aria-hidden
              className={cn(
                'absolute left-[calc(50%+1rem)] right-[calc(-50%+1rem)] top-4 h-px',
                complete ? 'bg-accent' : 'bg-border-strong',
              )}
            />
          )}
          <span
            aria-hidden
            className={cn(
              'relative z-10 mx-auto flex size-8 items-center justify-center rounded-full border text-xs font-semibold',
              complete && 'border-accent bg-accent text-accent-ink',
              active && 'border-accent bg-accent-subtle text-accent',
              !complete &&
                !active &&
                'border-border-strong bg-canvas text-ink-muted',
            )}
          >
            {complete ? <Check className="size-4" /> : index + 1}
          </span>
          <span
            className={cn(
              'mt-2 block truncate px-1 text-xs',
              active ? 'font-semibold text-ink' : 'text-ink-muted',
            )}
          >
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

const SetupForm = ({
  project,
  edit = false,
  growth = false,
}: {
  project?: Project;
  edit?: boolean;
  growth?: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phase, setPhase] = React.useState<'website' | 'review' | 'install'>(
    edit
      ? 'review'
      : project?.profile?.setupStep === 'install' ||
          project?.profile?.setupStep === 'complete'
        ? 'install'
        : 'website',
  );
  const [website, setWebsite] = React.useState(
    project?.profile?.websiteUrl ||
      (project?.primaryDomain ? `https://${project.primaryDomain}` : ''),
  );
  const [name, setName] = React.useState(
    project?.name === 'Default' ? '' : (project?.name ?? ''),
  );
  const [description, setDescription] = React.useState(
    project?.profile?.description ?? '',
  );
  const [logoUrl, setLogoUrl] = React.useState(project?.profile?.logoUrl ?? '');
  const [platform, setPlatform] = React.useState<Platform | null>(
    project?.profile?.platform ?? null,
  );
  const [discovery, setDiscovery] = React.useState<WebsiteDiscovery | null>(
    null,
  );
  const [additionalHosts, setAdditionalHosts] = React.useState('');
  const [selectedHosts, setSelectedHosts] = React.useState<string[]>([]);
  const matchingDiscovery = discoveryMatchesWebsite(discovery, website)
    ? discovery
    : null;
  const websiteChanged =
    normalizedWebsite(website) !==
    normalizedWebsite(project?.profile?.websiteUrl);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [websiteError, setWebsiteError] = React.useState<string | null>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => {
    headingRef.current?.focus();
  }, [phase]);
  const applyDiscovery = (result: WebsiteDiscovery) => {
    setDiscovery(result);
    setSelectedHosts(
      (result.suggestedHostnames ?? []).filter(
        (host) =>
          !/(^|[.-])(demo|staging|preview|dev|test)([.-]|$)/i.test(host),
      ),
    );
    setAdditionalHosts('');
    setWebsite(result.websiteUrl);
    setName(result.name);
    setDescription(result.description ?? '');
    setLogoUrl(result.logoUrl ?? '');
    setPlatform(result.suggestedPlatform);
    setPhase('review');
  };
  const discover = useWebsiteDiscovery(project?.id ?? 'new', applyDiscovery);
  React.useEffect(() => {
    if (phase === 'website' && discover.job && !website.trim()) {
      setWebsite(discover.job.websiteUrl);
    }
  }, [discover.job, phase, website]);
  const save = useMutation({
    mutationFn: async (options?: { allowDuplicate?: boolean }) => {
      const websiteUrl = normalizedWebsite(website);
      if (website.trim() && !websiteUrl) {
        throw new Error(
          'Enter a valid public website address, or clear it to continue without one.',
        );
      }
      const domain = websiteUrl ? new URL(websiteUrl).hostname : null;
      const profile: ProjectProfile = {
        ...project?.profile,
        websiteUrl,
        logoUrl: logoUrl.trim() || null,
        description: description.trim() || null,
        platform: platform!,
        setupStep: 'install',
        ...(matchingDiscovery
          ? {
              websiteResearch: matchingDiscovery.websiteResearch ?? null,
              suggestedTrackingPlan:
                matchingDiscovery.suggestedTrackingPlan ?? null,
              suggestedMonitoringTerms:
                matchingDiscovery.suggestedMonitoringTerms ?? [],
            }
          : websiteChanged
            ? {
                websiteResearch: null,
                suggestedTrackingPlan: null,
                suggestedMonitoringTerms: [],
              }
            : {}),
      };
      const body = {
        name: name.trim(),
        primaryDomain: domain,
        profile,
        allowedHostnames: setupHostnames(
          websiteUrl,
          matchingDiscovery ? selectedHosts : [],
          project,
          parseAdditionalHostnames(additionalHosts),
        ),
        timezone:
          project?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'UTC',
        ...(options?.allowDuplicate ? { allowDuplicate: true } : {}),
      };
      return project
        ? projectApi.updateProject(project.id, body)
        : projectApi.createProject(body);
    },
    onSuccess: async (updated) => {
      discover.reset();
      queryClient.setQueryData(['project', updated.id], updated);
      queryClient.removeQueries({ queryKey: ['setup-key', updated.id] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${updated.id}/setup`, { replace: true });
    },
  });
  const duplicateProject = React.useMemo(() => {
    if (!(save.error instanceof ApiError) || save.error.status !== 409) {
      return null;
    }
    const details = save.error.details as
      | {
          code?: unknown;
          project?: { id?: unknown; name?: unknown; primaryDomain?: unknown };
        }
      | undefined;
    if (
      details?.code !== 'project_domain_exists' ||
      typeof details.project?.id !== 'string' ||
      typeof details.project.name !== 'string'
    ) {
      return null;
    }
    return {
      id: details.project.id,
      name: details.project.name,
      primaryDomain:
        typeof details.project.primaryDomain === 'string'
          ? details.project.primaryDomain
          : null,
    };
  }, [save.error]);
  const showGrowth =
    growth && phase === 'install' && Boolean(project?.profile?.trackingPlan);
  const phaseIndex = showGrowth
    ? 3
    : phase === 'website'
      ? 0
      : phase === 'review'
        ? 1
        : 2;
  return (
    <div className="mx-auto max-w-2xl pb-10 pt-3 sm:pt-8">
      <div className="mb-8 border-b border-border pb-7">
        <div className="px-1 sm:px-4">
          <SetupProgress current={phaseIndex} />
        </div>
      </div>
      {phase === 'install' && project ? (
        showGrowth ? (
          <GrowthSignals project={project} />
        ) : (
          <ProjectInstallation project={project} />
        )
      ) : phase === 'website' ? (
        <div className="py-4 sm:py-8">
          <span className="mb-5 inline-flex size-11 items-center justify-center rounded-xl bg-accent-subtle">
            <Globe2 className="size-5 text-accent" />
          </span>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-3xl font-semibold tracking-tight text-ink outline-none sm:text-4xl"
          >
            Start with your website
          </h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-ink-muted">
            We’ll prepare your analytics setup.
          </p>
          <form
            noValidate
            className="mt-8 grid items-start gap-3 sm:grid-cols-[minmax(0,1fr)_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              if (!website.trim()) {
                setWebsiteError('Enter your website address to continue.');
                return;
              }
              setWebsiteError(null);
              discover.mutate(website);
            }}
          >
            <Field
              label="Website address"
              htmlFor="setup-website"
              error={websiteError}
            >
              <Input
                id="setup-website"
                name="website"
                autoComplete="url"
                inputMode="url"
                placeholder="yourproduct.com"
                value={website}
                onChange={(event) => {
                  setWebsite(event.target.value);
                  setWebsiteError(null);
                  discover.reset();
                }}
                maxLength={2048}
                disabled={discover.isPending}
                className="h-12 text-base placeholder:text-ink-muted"
              />
            </Field>
            <Button
              size="lg"
              type="submit"
              className="h-12 w-full sm:mt-[1.625rem] sm:w-auto"
              disabled={discover.isPending}
            >
              {discover.isPending ? 'Preparing…' : 'Set up analytics'}
              {!discover.isPending && <ArrowRight />}
            </Button>
            {discover.isError && (
              <Alert tone="error" className="sm:col-span-2">
                {discover.error?.message}
              </Alert>
            )}
            {discover.isError &&
              Boolean(
                discover.job?.result?.websiteResearch?.sources.length,
              ) && (
                <Button
                  type="button"
                  variant="secondary"
                  className="sm:col-span-2 sm:justify-self-start"
                  onClick={() => {
                    const result = discover.job!.result!;
                    const warning = discover.error?.message;
                    discover.reset();
                    applyDiscovery({
                      ...result,
                      warnings: [
                        ...result.warnings,
                        ...(warning ? [warning] : []),
                      ],
                    });
                  }}
                >
                  Continue with available details
                </Button>
              )}
            {discover.isPending && (
              <div className="sm:col-span-2">
                <DiscoveryProgress job={discover.job} />
              </div>
            )}
          </form>
          <button
            type="button"
            disabled={discover.isPending}
            onClick={() => {
              setFormError(null);
              setWebsite('');
              discover.reset();
              setDiscovery(null);
              setSelectedHosts([]);
              setPhase('review');
            }}
            className="mt-5 py-2 text-sm text-ink-muted underline decoration-border-strong underline-offset-4 transition-colors hover:text-ink disabled:opacity-50"
          >
            No website yet? Continue without one
          </button>
        </div>
      ) : (
        <form
          className="space-y-7"
          onSubmit={(event) => {
            event.preventDefault();
            setFormError(null);
            if (!platform) {
              setFormError('Choose where people use your product.');
              return;
            }
            save.mutate();
          }}
        >
          <div>
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="text-3xl font-semibold tracking-tight text-ink outline-none"
            >
              {matchingDiscovery &&
              matchingDiscovery.websiteResearch?.sources.length
                ? 'Confirm your product'
                : 'Tell us about your product.'}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-ink-muted">
              {matchingDiscovery &&
              matchingDiscovery.websiteResearch?.sources.length
                ? 'Check these details so we set up the right tracking.'
                : 'A few details help us show you the right setup and reports.'}
            </p>
          </div>
          {matchingDiscovery?.warnings.map((warning) => (
            <Alert key={warning}>{warning}</Alert>
          ))}
          {matchingDiscovery?.websiteResearch?.sources.length === 0 && (
            <Button
              type="button"
              variant="secondary"
              loading={discover.isPending}
              onClick={() => discover.mutate(website)}
            >
              Retry website analysis
            </Button>
          )}
          {discover.isPending && <DiscoveryProgress job={discover.job} />}
          {discover.isError && (
            <Alert tone="error">{discover.error?.message}</Alert>
          )}
          <div className="flex items-center gap-4 border-y border-border py-5">
            <ProjectAvatar
              name={name}
              logoUrl={logoUrl}
              className="size-16 text-xl"
            />
            <div className="min-w-0">
              <p className="break-words text-lg font-semibold text-ink">
                {name || 'Your product'}
              </p>
              <p className="mt-1 break-all text-sm text-ink-muted">
                {website || 'Your workspace, ready for what’s next.'}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <Field label="Product name" htmlFor="setup-name">
              <Input
                id="setup-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={200}
                placeholder="Your product name"
                className="placeholder:text-ink-muted"
              />
            </Field>
            <Field
              className="[&_p]:text-ink-muted"
              label="What does it do?"
              htmlFor="setup-description"
              hint="Optional. Helps give your workspace context."
            >
              <textarea
                id="setup-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={400}
                rows={2}
                className="w-full resize-y rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm leading-relaxed text-ink"
              />
            </Field>
          </div>
          <fieldset>
            <legend className="mb-3 text-sm font-semibold text-ink">
              Where do people use your product?
            </legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {PLATFORMS.map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    'relative flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors',
                    platform === option.value
                      ? 'border-accent bg-accent-subtle'
                      : 'border-border bg-surface hover:border-border-strong',
                  )}
                >
                  <input
                    type="radio"
                    name="platform"
                    value={option.value}
                    checked={platform === option.value}
                    onChange={() => setPlatform(option.value)}
                    className="peer sr-only"
                  />
                  <span
                    className="pointer-events-none absolute inset-0 rounded-xl peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent"
                    aria-hidden
                  />
                  <option.icon className="size-5 shrink-0 text-accent" />
                  <span>
                    <span className="block text-sm font-semibold text-ink">
                      {option.label}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-ink-muted">
                      {option.detail}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {discovery?.appStoreUrls.length ? (
              <p className="mt-3 text-sm text-ink-muted">
                We found app-store links. Choose Both if you also have a browser
                app.
              </p>
            ) : null}
          </fieldset>
          {matchingDiscovery &&
            (matchingDiscovery.suggestedHostnames?.length ?? 0) > 0 && (
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-ink">
                  Where tracking should work
                </legend>
                <p className="text-sm text-ink-muted">
                  We found linked app or account domains. Include the ones where
                  people use your product.
                </p>
                {matchingDiscovery.suggestedHostnames!.map((host) => (
                  <label
                    key={host}
                    className="flex items-center gap-3 text-sm text-ink"
                  >
                    <input
                      type="checkbox"
                      checked={selectedHosts.includes(host)}
                      onChange={(event) =>
                        setSelectedHosts((previous) =>
                          event.target.checked
                            ? [...previous, host]
                            : previous.filter((item) => item !== host),
                        )
                      }
                      className="size-4 shrink-0 accent-accent"
                    />
                    <span className="break-all">{host}</span>
                  </label>
                ))}
              </fieldset>
            )}
          <Field
            label="Other app domains"
            htmlFor="setup-app-domains"
            hint="Optional. Add exact domains where people use your product, separated by commas."
          >
            <Input
              id="setup-app-domains"
              value={additionalHosts}
              onChange={(event) => setAdditionalHosts(event.target.value)}
              placeholder="app.example.com"
              maxLength={2000}
            />
          </Field>
          {discovery && !matchingDiscovery && website.trim() && (
            <Alert>
              The website changed. Review it again to get matching
              recommendations.
              <Button
                type="button"
                variant="secondary"
                className="mt-3"
                loading={discover.isPending}
                onClick={() => discover.mutate(website)}
              >
                Review this website
              </Button>
            </Alert>
          )}
          <details className="rounded-lg border border-border px-4 py-3">
            <summary className="cursor-pointer text-sm font-medium text-ink">
              Website and logo
            </summary>
            <div className="mt-4 space-y-4">
              <Field
                className="[&_p]:text-ink-muted"
                label="Website"
                htmlFor="review-website"
                hint="Optional for products that aren’t published yet."
              >
                <Input
                  id="review-website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  maxLength={2048}
                  placeholder="yourproduct.com"
                  className="placeholder:text-ink-muted"
                />
              </Field>
              <Field
                className="[&_p]:text-ink-muted"
                label="Logo URL"
                htmlFor="review-logo"
                hint="Use a public image URL, or leave empty to use your product’s initial."
              >
                <Input
                  id="review-logo"
                  type="url"
                  value={logoUrl}
                  onChange={(event) => setLogoUrl(event.target.value)}
                  maxLength={2048}
                  placeholder="https://yourproduct.com/logo.png"
                  className="placeholder:text-ink-muted"
                />
              </Field>
            </div>
          </details>
          {duplicateProject ? (
            <Alert
              tone="warning"
              title={`${duplicateProject.name} is already in GainSignal`}
            >
              <p>
                This workspace already uses{' '}
                {duplicateProject.primaryDomain ?? 'the same domain'}.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => navigate(`/projects/${duplicateProject.id}`)}
                >
                  Open {duplicateProject.name}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={save.isPending}
                  onClick={() => save.mutate({ allowDuplicate: true })}
                >
                  Create another anyway
                </Button>
              </div>
            </Alert>
          ) : formError || save.isError ? (
            <Alert tone="error">{formError || save.error?.message}</Alert>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
            <Button
              type="button"
              variant="ghost"
              disabled={save.isPending}
              onClick={() => setPhase('website')}
            >
              <ArrowLeft />
              Change website
            </Button>
            <Button type="submit" size="lg" loading={save.isPending}>
              Prepare my setup
              <ArrowRight />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export const NewProjectPage = () => {
  const { projectId } = useParams();
  const [search] = useSearchParams();
  const { user } = useSession();
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.getProject(projectId!),
    enabled: Boolean(projectId),
  });
  const canManage = ['owner', 'admin'].includes(
    user?.currentOrganization.role ?? '',
  );
  if (!canManage) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <Alert>Ask an organization admin to set up this project.</Alert>
        <Button asChild variant="ghost" className="mt-4">
          <Link to="/?explore=1">Back to projects</Link>
        </Button>
      </div>
    );
  }
  if (projectId && project.isPending) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        <Skeleton className="h-80" />
      </div>
    );
  }
  if (projectId && (project.isError || !project.data)) {
    return (
      <div className="mx-auto max-w-xl py-12">
        <Alert tone="error">
          We couldn’t load this project.{' '}
          <button className="underline" onClick={() => void project.refetch()}>
            Try again
          </button>
        </Alert>
      </div>
    );
  }
  return (
    <SetupForm
      key={`${projectId ?? 'new'}:${search.get('edit')}:${project.data?.profile?.setupStep}`}
      project={project.data}
      edit={search.get('edit') === '1'}
      growth={search.get('step') === 'growth'}
    />
  );
};
